import { ActionType } from '../../action-types';
import { Action } from '../../actions/index';
import produce from 'immer';

// import functions
// import calcDSViewFrontendInputData from './calculators/calcDSViewFrontendInputData';

// import interfaces & data
// import { PaperFieldLevel, MatrixDataRange } from '.././../../types/data.global';
import { InteractionTypes, TooltipTypes, MainViewTypes } from '../../../types/data';
import { ImpactType, CitationFlowType, GrantGlyphType } from '../../../types/data.global';

interface InitialState {
  isShowPredictionGraph: boolean,
  isShowHistoricalGraph: boolean,

  highlghtedGrantFieldID: string,
  isInitializeBubbleTreemapFinished: boolean,
  isOptimizedBubbleTreemapFinished: boolean,

  cleanMainviewGraph: boolean, // whether to the mainview graph element is clean (empty)
  isQueryViewFolded: boolean,

  // 1. main view
  mainviewGraphDivSize: { width: number, height: number },
  svgSize: { width: number, height: number },
  glyphStyle: MainViewTypes.GlyphStyle,
  grantTypeNodeTransformXY: {
    x: number,
    y: number,
  },
  sharedBubbleTreemapStyle: MainViewTypes.BubbleTreemapStyle,
  isShowLeafNode: boolean;
  contextMenuImpactEntity: string;
  isShowContextMenu: boolean;
  contextMenuPosition: { left: number, top: number };
  wordleEntity: { entity: string, fieldID: string };
  wordlePosition: { left: number; top: number };
  isShowWordle: boolean;
  selectedImpactEntityField: { [entity: string]: string };
  isShowHistoricalFlow: boolean;
  isShowPredictionFlow: boolean;
  clearCheckboxSelection: boolean;
  showEntityFieldLevelGraph: boolean,
  showEntityTypeLevelGraph: boolean,
  mainviewContextMenu: TooltipTypes.MainviewContextMenu;
  fieldLevelRange: InteractionTypes.UIParamFieldLevelRangeDict; // 对应backend: `field_level_dict`
  citationFlowType: string;
  impactType: string;
  impactStatisticsTypeList: string[];
  grantGlyphType: string;
  topKField: number;
  topKCitationFlow: number;
  updatedImpactEntityType: string;
  entityTopicNodeDict: InteractionTypes.UIParamEntityTopicNodeDict;

  // --- user management ---
  authState: {
    token: string | null,
    isAuthenticated: boolean | null,
    loading: boolean | null,
    user: string | null,
    error: null | string
  };
  registerState: {
    registerStatus: boolean,
    error: string | null
  };
  deleteAccount: {
    deleteStatus: boolean,
    error: string | null
  };
  forgetPassword: {
    forgetStatus: boolean,
    error: string | null
  };
  resetPassword: {
    resetStatus: boolean,
    error: string | null
  };
  verifyEmail: {
    verifyStatus: boolean,
    error: string | null
  };
}

const initialState: InitialState = {
  isShowHistoricalGraph: true,
  isShowPredictionGraph: false,
  highlghtedGrantFieldID: 'all',
  sharedBubbleTreemapStyle: {
    x: 0,
    y: 0,
    radius: 0,
    padding: {
      outerPadding: 0,
      leafNodePadding: 0,
    },
    leafNodeSizeScaleType: 'pow',
    powParam: 0.6,
  },
  mainviewGraphDivSize: { width: 0, height: 0 },
  svgSize: { width: 0, height: 0 },
  glyphStyle: {
    svgR: 0, // svgSize.mainview.height / 2,
    grantGlyphGroupR: 0, // 180, // grant type node整个大小
    glyphMaxR: 0, // 60, // temp, unified glyph size. TODO: scale the group with total_impact
    nodePadding: 0, // 5,
    glyphRRangeRatio: { min: 0.4, max: 1 },
    minR_Ratio: { directImpact: 0.2, indirectImpact: 0.5, },
    RDifference_Ratio: { directImpact: 0.3, indirectImpact: 0.5, },  // value范围在[minR_Ratio, minR_Ratio + RDifference_Ratio]之间
    collideStrength: 1,
  },
  grantTypeNodeTransformXY: {
    x: 0,
    y: 0,
  },
  isInitializeBubbleTreemapFinished: false,
  isOptimizedBubbleTreemapFinished: false,

  cleanMainviewGraph: false,
  isQueryViewFolded: false,
  isShowLeafNode: true,
  contextMenuImpactEntity: '',
  isShowContextMenu: false,
  contextMenuPosition: { left: 0, top: 0 },
  wordleEntity: { entity: '', fieldID: '' },
  wordlePosition: { left: 0, top: 0 },
  isShowWordle: false,
  selectedImpactEntityField: {},
  isShowHistoricalFlow: true,
  isShowPredictionFlow: false,
  clearCheckboxSelection: false,
  showEntityFieldLevelGraph: false,
  showEntityTypeLevelGraph: false,
  mainviewContextMenu: {
    showContextMenu: false,
    x: 0,
    y: 0,
  },
  fieldLevelRange: {  // backend: `field_level_dict`
    'grant': ['L1'],  // ['L0', 'L1'], // L0-L1, 理论上应该只选一个固定的level
    'paper': ['L0', 'L1'], // L0-L4
    'patent': ['L0', 'L1', 'L2'], // L0-L2
    'clinical_trial': ['L0', 'L1', 'L2', 'L3'],
    'newsfeed': ['L0', 'L1'], // 2-levels: 'Country', 'Outlet or Author'

    // case1-sample-20%
    'policy': ['L0', 'L1'], // for case1-sample data (online demo)

    // // case1-all, and case2
    // 'policy': ['L0', 'L1', 'L2', 'L3'], // for case1-paper data (paper-case1)
  },
  citationFlowType: CitationFlowType.HISTORICAL,
  impactType: ImpactType.INDIRECT_IMPACT_FOLDED, // 默认选indirect impact
  topKField: 10,
  topKCitationFlow: 10,
  impactStatisticsTypeList: ['patent', 'clinical_trial', 'policy'], // 默认选indirect impact的三个type
  updatedImpactEntityType: '',
  grantGlyphType: GrantGlyphType.RCIGLYPH,
  entityTopicNodeDict: {
    "grant": "all",
    "paper": "all",
    "patent": "all",
    "clinical_trial": "all",
    "policy": "all",
    "newsfeed": "all",
  },
  // --- user management ---
  authState: {
    token: sessionStorage.getItem('token'),
    isAuthenticated: false,
    loading: false,
    user: null,
    error: null
  },
  registerState: {
    registerStatus: false,
    error: null
  },
  deleteAccount: {
    deleteStatus: false,
    error: null
  },
  forgetPassword: {
    forgetStatus: false,
    error: null
  },
  resetPassword: {
    resetStatus: false,
    error: null
  },
  verifyEmail: {
    verifyStatus: false,
    error: null
  }
}

const reducer = produce((state: InitialState = initialState, action: Action) => {
  switch (action.type) {
    case ActionType.IS_SHOW_PREDICTION_GRAPH:
      state.isShowPredictionGraph = action.isShowPredictionGraph
      break;
    case ActionType.IS_SHOW_HISTORICAL_GRAPH:
      state.isShowHistoricalGraph = action.isShowHistoricalGraph
      break;
    case ActionType.UPDATE_HIGHLGHTEED_GRANT_FIELD_ID:
      state.highlghtedGrantFieldID = action.payload
      break;
    case ActionType.UPDATE_MAINVIEW_GRAPH_DIV_SIZE:
      // (1) update svg size (the same as the parent div)
      state.mainviewGraphDivSize = action.payload
      state.svgSize = action.payload

      // (2) update `glyphStyle`
      const svgWidth = action.payload.width
      const svgHeight = action.payload.height
      state.glyphStyle.svgR = Math.min(svgWidth, svgHeight) / 2
      state.glyphStyle.grantGlyphGroupR = state.glyphStyle.svgR * 0.35 // 0.35: temp, hard code
      state.glyphStyle.glyphMaxR = state.glyphStyle.svgR * 0.12 // 0.12: temp, hard code
      state.glyphStyle.nodePadding = state.glyphStyle.svgR * 0.01 // 0.01: temp, hard code
      // keep other parameters unchanged

      // (3) update `grantTypeNodeTransformXY`
      state.grantTypeNodeTransformXY.x = svgWidth / 2
      state.grantTypeNodeTransformXY.y = svgHeight / 2

      // (4) update `sharedBubbleTreemapStyle`
      // opt 4 - case 1 - NIH, final
      state.sharedBubbleTreemapStyle = {
        x: svgWidth / 2,
        y: svgHeight / 2,
        radius: 1550, // temp, hard code
        padding: {
          outerPadding: 6, // temp, hard code
          leafNodePadding: 28, // temp, hard code
        },
        leafNodeSizeScaleType: 'pow',   // 'pow', 'log', 'linear', // temp, hard code
        powParam: 0.6, // temp, hard code
      }
      break;
    case ActionType.UPDATE_IS_INITIALIZE_BUBBLE_TREEMAP_FINISHED:
      state.isInitializeBubbleTreemapFinished = action.payload
      break;
    case ActionType.UPDATE_IS_OPTIMIZED_BUBBLE_TREEMAP_FINISHED:
      state.isOptimizedBubbleTreemapFinished = action.payload
      break;
    case ActionType.CLEAN_MAINVIEW_GRAPH:
      state.cleanMainviewGraph = action.payload
      break;
    case ActionType.SET_QUERY_VIEW_FOLDED:
      state.isQueryViewFolded = action.payload
      break;
    case ActionType.IS_SHOW_LEAF_NODE:
      state.isShowLeafNode = action.isShowLeafNode
      break;
    case ActionType.UPDATE_FIELD_LEVEL_RANGE:
      state.fieldLevelRange = action.fieldLevelRange
      break;
    case ActionType.UPDATE_CONTEXT_MENU_IMPACT_ENTITY:
      state.contextMenuImpactEntity = action.contextMenuImpactEntity
      break;
    case ActionType.UPDATE_CONTEXT_MENU_POSITION:
      state.contextMenuPosition = action.contextMenuPosition
      break;
    case ActionType.IS_SHOW_CONTEXT_MENU:
      state.isShowContextMenu = action.isShowContextMenu
      break;
    case ActionType.UPDATE_WORDLE_ENTITY:
      state.wordleEntity = action.wordleEntity
      break;
    case ActionType.UPDATE_WORDLE_POSITION:
      state.wordlePosition = action.wordlePosition
      break;
    case ActionType.IS_SHOW_WORDLE:
      state.isShowWordle = action.isShowWordle
      break;
    case ActionType.UPDATE_SELECTED_IMPACT_ENTITY_FIELD:
      state.selectedImpactEntityField = action.payload
      break;
    case ActionType.IS_SHOW_PREDICTION_FLOW:
      state.isShowPredictionFlow = action.isShowPredictionFlow
      break;
    case ActionType.IS_SHOW_HISTORICAL_FLOW:
      state.isShowHistoricalFlow = action.isShowHistoricalFlow
      break;
    case ActionType.CLEAR_CHECKBOX_SELECTION:
      state.clearCheckboxSelection = action.payload
      break;
    case ActionType.SET_SHOW_ENTITY_FIELD_LEVEL_GRAPH:
      state.showEntityFieldLevelGraph = action.payload;
      break;
    case ActionType.SET_SHOW_ENTITY_TYPE_LEVEL_GRAPH:
      state.showEntityTypeLevelGraph = action.payload;
      break;
    case ActionType.SET_CONTEXT_MENU_VISIBILITY_AND_POSITION:
      state.mainviewContextMenu = action.payload
      break
    case ActionType.UPDATE_TOP_K_CITATION_FLOW:
      state.topKCitationFlow = action.topKCitationFlow
      break
    case ActionType.UPDATE_TOP_K_FIELD:
      state.topKField = action.topKField
      break
    case ActionType.UPDATE_GRANT_GLYPH_TYPE:
      state.grantGlyphType = action.grantGlyphType
      break
    case ActionType.UPDATE_CITATION_FLOW_TYPE:
      // console.log('ui reducer: update Citation Flow Type to: ', action.citationFlowType)
      state.citationFlowType = action.citationFlowType
      // state.paperFieldLevel = action.paperFieldLevel
      // state.paperFieldL0Name = action.paperFieldL0Name
      break
    case ActionType.UPDATE_IMPACT_TYPE:
      // console.log('ui reducer: update Impact Type to: ', action.impactType)
      state.impactType = action.impactType
      break
    case ActionType.UPDATE_IMPACT_STATISTICS_TYPE_LIST:
      // console.log('ui reducer: update Impact Statistics Type List to: ', action.impactStatisticsTypeList)
      state.impactStatisticsTypeList = action.impactStatisticsTypeList
      break;
    // --- user management ---
    case ActionType.PERFORM_LOGIN:
      state.authState.isAuthenticated = action.isAuthenticated;
      state.authState.error = action.error;
      state.authState.token = action.token;
      state.authState.user = action.user;
      break;
    case ActionType.PERFORM_REGISTER:
      state.registerState.error = action.error;
      state.registerState.registerStatus = action.registerStatus;
      break;
    case ActionType.PERFORM_DELETE_ACCOUNT:
      state.deleteAccount.error = action.error;
      state.deleteAccount.deleteStatus = action.deleteStatus;
      break;
    case ActionType.PERFORM_FORGET_PASSWORD:
      state.forgetPassword.error = action.error;
      state.forgetPassword.forgetStatus = action.forgetStatus;
      break;
    case ActionType.PERFORM_RESET_PASSWORD:
      state.resetPassword.error = action.error;
      state.resetPassword.resetStatus = action.resetStatus;
      break;
    case ActionType.PERFORM_VERIFY_EMAIL:
      state.verifyEmail.error = action.error;
      state.verifyEmail.verifyStatus = action.verifyStatus;
      break;
    default:
      break
  }
  return state
})

export default reducer;